import React from 'react'
import { Link } from 'react-router-dom'
import './sso-workspace.scss'
import './auth-card.scss'
import { ProviderLogo } from './provider-logo'
import { Pill } from './pill'
import { IconUser } from '../icon/icon'

// The one layout every sign-in journey page shares (/auth/email,
// /auth/providers, /auth/error, the password pages, /open/organization,
// /open/error): a centred card on the sunk ground, in the connection
// workspace's tokens so light and dark both follow from sso-workspace.scss.
// Pages compose these pieces rather than styling their own, so the journey
// reads as one product.

export const AuthPage = ({ children, wide, labelledBy }) =>
  <div className="sw-workspace sa-page">
    <main className={`sa-card ${wide ? 'sa-wide' : ''}`.trim()} aria-labelledby={labelledBy}>
      {children}
    </main>
  </div>

// The page's heading. `mark` (an AuthMark) sits beside the title; `lede` is
// one or two sentences under it. `center` centres both, under an AuthScene.
export const AuthHead = ({ id, title, lede, mark, center }) =>
  <header className={`sa-head ${center ? 'sa-head-center' : ''}`.trim()}>
    {mark
      ? <div className="sa-head-row">
          {mark}
          <h1 id={id}>{title}</h1>
        </div>
      : <h1 id={id}>{title}</h1>}
    {lede && <p>{lede}</p>}
  </header>

// An outcome's large mark (owner 2026-10-24: the "you're signed in" pages
// looked "basic and sad and empty"): a disc in the tone with its glyph --
// a check that draws itself in, the alert's bar and dot, or a clock -- and a
// soft ring that pulses once. Under a second in all, and still when the
// person asked for reduced motion (`still`, and the CSS media query).
// Decorative: the page's title says what happened.
export const AUTH_OUTCOME_GLYPHS = ['check', 'alert', 'clock']

export const AuthOutcomeMark = ({ tone = 'ok', glyph = 'check', still }) =>
  <span className={`sa-omark ${tone} ${still ? 'is-still' : ''}`.replace(/\s+/g, ' ').trim()} data-glyph={glyph} aria-hidden="true">
    <svg className="sa-omark-svg" viewBox="0 0 52 52" focusable="false">
      <circle className="sa-omark-disc" cx="26" cy="26" r="24" />
      {glyph === 'check' && <path className="sa-omark-stroke sa-omark-check" d="M15.5 26.5l7 7 14-14" />}
      {glyph === 'alert' &&
        <>
          <path className="sa-omark-stroke sa-omark-bar" d="M26 15.5v13" />
          <circle className="sa-omark-dot" cx="26" cy="35.5" r="2.1" />
        </>}
      {glyph === 'clock' &&
        <>
          <circle className="sa-omark-stroke sa-omark-face" cx="26" cy="26" r="11.5" />
          <path className="sa-omark-stroke sa-omark-hands" d="M26 19.5V26l4.5 3" />
        </>}
    </svg>
  </span>

// An outcome's picture (owner 2026-10-24: "an animation and icons /
// illustrations"): where the person is (`from`: this browser), the mark, and
// where it goes (`to`: the EducationXR app, the other device), joined by two
// lines that draw across before the mark lands. `broken` (an error, an
// expired link) leaves the lines dashed and still. `from` / `to` are small
// pieces of art (AuthTile). Reads the person's motion setting itself, so the
// still version is the one rendered (data-motion), not only the one styled.
export const AuthScene = ({ from, to, tone = 'ok', glyph = 'check', broken, reducedMotion }) =>
  <div className={`sa-scene ${tone} ${broken ? 'is-broken' : ''}`.replace(/\s+/g, ' ').trim()} data-motion={reducedMotion ? 'reduced' : 'full'} data-testid="auth-scene" aria-hidden="true">
    {from && <span className="sa-scene-end sa-scene-from">{from}</span>}
    {from && <span className="sa-scene-line sa-scene-line-in" />}
    <AuthOutcomeMark tone={tone} glyph={glyph} still={reducedMotion} />
    {to && <span className="sa-scene-line sa-scene-line-out" />}
    {to && <span className="sa-scene-end sa-scene-to">{to}</span>}
  </div>

// One end of an AuthScene: a soft tile holding an icon or an image.
export const AuthTile = ({ children, label }) =>
  <span className="sa-tile" title={label}>
    {children}
  </span>

// The quiet last line of an outcome page ("You can close this window.").
export const AuthCloseHint = ({ children = 'You can close this window.' }) =>
  <p className="sa-close-hint">{children}</p>

// A round tone mark (ok, warn, bad) beside an outcome's title.
export const AuthMark = ({ tone, children }) =>
  <span className={`sa-mark ${tone || ''}`.trim()} aria-hidden="true">{children}</span>

// The rule between two ways in: a line, the word, a line.
export const AuthDivider = ({ children = 'or' }) =>
  <div className="sa-divider" role="separator" aria-label={typeof children === 'string' ? children : undefined}>
    <span>{children}</span>
  </div>

// A tinted note. tone: info (default), warn, bad. Status by default, so a
// screen reader announces it when it appears.
export const AuthNotice = ({ tone, role = 'status', id, children }) =>
  <div className={`sa-notice ${tone || ''}`.trim()} role={role} id={id}>
    {children}
  </div>

// A working state: a small ring (still under reduced motion) and a sentence.
export const AuthLoading = ({ children }) =>
  <p className="sa-loading" role="status">
    <span className="sa-spin" aria-hidden="true" />
    <span>{children}</span>
  </p>

// The email being signed in, as a quiet read-only field, with "Change" as a
// small secondary button back to the email step.
export const AuthWho = ({ email, changeTo }) =>
  <div className="sa-who">
    <span className="sa-who-icon" aria-hidden="true"><IconUser /></span>
    <span className="sa-who-email" title={email || ''}>{email}</span>
    {changeTo &&
      <Link className="btn sw-btn sa-who-change" to={changeTo} aria-label="Use a different email">Change</Link>}
  </div>

// Handing off to a connection: its tile and the working line (which names it),
// centred in an inset panel.
export const AuthHandoff = ({ providerLink, children }) =>
  <div className="sa-handoff">
    <ProviderLogo iconSVG={providerLink && providerLink.iconSVG} iconSVGDark={providerLink && providerLink.iconSVGDark} />
    <AuthLoading>{children}</AuthLoading>
  </div>

// The connection a message is about: its logo tile and its name.
export const AuthConnection = ({ id, providerLink, prefix }) =>
  providerLink && providerLink.displayName
    ? <div className="sa-identity" id={id}>
        <ProviderLogo iconSVG={providerLink.iconSVG} iconSVGDark={providerLink.iconSVGDark} />
        <span>
          {prefix && <>{prefix} </>}
          <strong>{providerLink.displayName}</strong>
        </span>
      </div>
    : null

// One choice in a list of connections or organizations. `logo` is whatever
// tile the list draws (ProviderLogo, an organization logo, an initial).
// `flag` is a short marker pill beside the name ("Last used").
// `flagTone` colours the pill (default info). `action` is a word in place of
// the chevron ("Restore") when picking the row does something particular.
// `current`: the row is where the person already is (aria-current, a quiet
// surface, no chevron) -- not somewhere to go.
export const AuthOption = ({ logo, name, flag, flagTone, sub, action, current, disabled, onClick }) =>
  <button type="button" className={`sa-option ${current ? 'sa-option-current' : ''}`.trim()} aria-current={current ? 'true' : undefined} disabled={disabled} onClick={onClick}>
    {logo}
    <span className="sa-option-name">
      <span className="sa-option-title">
        <span>{name}</span>
        {flag && <Pill tone={flagTone || 'info'}>{flag}</Pill>}
      </span>
      {sub && <span className="sa-option-sub">{sub}</span>}
    </span>
    {action
      ? <span className="sa-option-action">{action}</span>
      : !current && <span className="sa-chev" aria-hidden="true">›</span>}
  </button>
